import { FloraButton } from '@grupoboticario/flora-react';
import { ArrowRightIcon } from '@grupoboticario/flora-react-icons';
import { Link } from 'react-router-dom';
import slugify from 'slugify';

import { ResponsiveFloraTypography } from 'components/ResponsiveFloraTypography';
import { useUrlTrackingParams, useTracking } from 'hooks';

import Styles from './CardContent.styles';

import type { CardContentProps } from './CardContent.d';

export const CardContent = ({
  image,
  description,
  title,
  buttonText,
  link,
  linkPortal = { url: '' },
  portalLink = { url: '' },
  analyticsData,
  trackButton,
}: CardContentProps & {
  linkPortal?: { url?: string };
  portalLink?: { url?: string };
  trackButton?: {
    section?: string;
    page?: string;
    isButtonTracking?: boolean;
  };
}) => {
  const linkWithTrackingParams = useUrlTrackingParams(
    link?.url || linkPortal?.url || portalLink?.url || ''
  );

  const { sendDataToGTM } = useTracking();

  const handleLinkRedirect = () => {
    if (trackButton?.isButtonTracking) {
      return null;
    }

    const analytics = {
      event: 'select_promotion',
      ecommerce: {
        promotion_name: slugify(title ?? description ?? '', { lower: true }),
        creative_name: slugify(image?.url ?? '', { lower: true }),
        creative_slot: analyticsData?.index ?? 0,
      },
      event_parameters: {
        cd_interaction_detail: slugify(`click:${buttonText}`, { lower: true }),
        cd_section: slugify(analyticsData?.section ?? '', { lower: true }),
      },
    };
    sendDataToGTM({
      ...analytics,
    });
  };

  return (
    <Styles.Card>
      <Styles.ImageWrapper justify='center' align='center'>
        <Styles.Image src={image?.url} />
      </Styles.ImageWrapper>
      <Styles.Content direction='column' gap='$2'>
        {title && <Styles.Title color='$backgroundAltPrimary'>{title}</Styles.Title>}
        {description && (
          <Styles.Description fontSize='bodySmallShort'>{description}</Styles.Description>
        )}

        {Boolean(link?.url || linkPortal?.url || portalLink?.url) && (
          <FloraButton
            as={Link}
            onClick={handleLinkRedirect}
            to={linkWithTrackingParams}
            target='_blank'
            rel='noopener noreferrer'
            has='iconRight'
            icon={<ArrowRightIcon />}
            hierarchy='secondary'
            isFull
            {...(trackButton?.isButtonTracking
              ? {
                  'autotagging-flora': 'on',
                  'data-flora': 'button',
                  'data-flora-text': `${trackButton.section}:${slugify(buttonText ?? '', {
                    lower: true,
                  })}`,
                }
              : {})}
          >
            <ResponsiveFloraTypography fontSize='bodySmallShort' fontWeight='bold'>
              {buttonText ?? 'Ver mais sobre'}
            </ResponsiveFloraTypography>
          </FloraButton>
        )}
      </Styles.Content>
    </Styles.Card>
  );
};
